import {
  Article,
  ArticleDeleteParams,
  ArticleEvent,
  ArticlePoll,
  ArticleType
} from '../../core/declarations/article.interface';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Clipboard } from '@capacitor/clipboard';

import {
  ArticleRepository,
  ArticleRepositoryParams
} from '../repositories/article.repository';
import { Like } from '../../core/declarations/like.interface';

import { PaginatedAbstractService } from './paginated.abstract.service';

import { ModalService } from '@bling-fe/shared/modals/base-modal';
import { Router } from '@angular/router';
import { ShareService } from './share.service';
import { select, Store } from '@ngrx/store';
import { marker as _ } from '@bling-fe/shared/utils';
import { Tenant } from '../../core/declarations/tenant.interface';
import {
  getCurrentTenant,
  getCurrentTenantApiUrl
} from '../../core/states/tenant-state/tenant.selectors';
import { getCurrentUser } from '../../core/states/user-state/user.selectors';
import { User } from '../../core/declarations/user.interface';
import { ReportService } from './report.service';
import { ArticleDetail } from '../../core/declarations/article-detail.interface';
import { CalendarService } from './calendar.service';
import {
  OptionsModalAction,
  OptionsModalProps,
  SharedModalsUiOptionsModalComponent
} from '../../modals/shared-modals-ui-options-modal/shared-modals-ui-options-modal.component';
import { NotificationService } from './notification.service';

@Injectable({
  providedIn: 'root'
})
export class PostService extends PaginatedAbstractService<ArticleRepositoryParams> {
  constructor(
    private articleRepository: ArticleRepository,
    private modalService: ModalService,
    private router: Router,
    private shareService: ShareService,
    private tenantStore: Store<Tenant>,
    private userStore: Store<User>,
    private reportService: ReportService,
    private calendarService: CalendarService,
    private notificationService: NotificationService
  ) {
    super(articleRepository);
  }

  vote(post: ArticlePoll, option: number) {
    return this.articleRepository.vote(post.id, option).pipe(
      tap(article => {
        this.data.replaceItem(article.id, article);
      })
    );
  }

  /**
   * If Like object is returned, like was added.
   * If null is returned, like was removed.
   */
  toggleLike(id: number): Observable<Like> {
    return this.articleRepository.like(id).pipe(
      tap(liked => {
        this.data.replaceItem(id, article => ({
          ...article,
          like_count: liked ? article.like_count + 1 : article.like_count - 1,
          liked: !!liked
        }));
      })
    );
  }

  togglePin(id: number): Observable<ArticleDetail> {
    return this.articleRepository.pin(id).pipe(
      tap(async post => {
        // the last pinned important item go to the first
        if (post.pinned) {
          if (post.important) {
            this.data.emitUpdateItems(items => {
              const i = items.findIndex(p => p.id === post.id);
              items.splice(i, 1);
              return [post, ...items];
            });
          } else {
            // the last pinnned non important item go to the one after the last pinned important
            this.data.emitUpdateItems(items => {
              const i = items.findIndex(p => p.id === post.id);
              const pinnedImportantItemcount = items
                .filter(p => p.important)
                .filter(p => p.pinned).length;

              items.splice(i, 1);
              items.splice(pinnedImportantItemcount, 0, post);
              return items;
            });
          }
        } else {
          // on unpinning we need to do refetch - we don't know on which page it lands
          await this.refetch();
        }
      })
    );
  }

  commentAdded(id: number) {
    this.data.replaceItem(id, it => ({
      ...it,
      comment_count: it.comment_count + 1
    }));
  }

  commentDeleted(id: number) {
    this.data.replaceItem(id, it => ({
      ...it,
      comment_count: it.comment_count - 1
    }));
  }

  private async getAppFrontendUrl(): Promise<string> {
    return firstValueFrom(
      this.tenantStore
        .select(getCurrentTenant)
        .pipe(map(t => t !== 'loading' && t.tenant_setting.app_frontend_url))
    );
  }

  private async getTenantApiUrl(): Promise<string> {
    return firstValueFrom(this.tenantStore.select(getCurrentTenantApiUrl));
  }

  async showAndHandlePostOptions(
    post: Article,
    groupParams?: { groupId: number; groupTitle: string }
  ): Promise<void> {
    const isDraft = post.is_draft;
    const actionsEnabled: OptionsModalAction[] = isDraft ? [] : ['share'];

    // Wait for current user data before proceeding
    const u = await firstValueFrom(this.userStore.pipe(select(getCurrentUser)));

    const isAdmin = u?.admin || u?.super_admin;
    const isPublisher = u?.id === post?.user?.id;
    // admin or super admin can delete with a reason any post
    // also once marked as important
    if (isPublisher) {
      if (post.article_type === ArticleType.EVENT) {
        // For events, always use deleteWithReason to handle both scenarios
        actionsEnabled.push('deleteWithReason');
      } else {
        // For other articles, use simple delete
        actionsEnabled.push('delete');
      }
    } else if (isAdmin) {
      actionsEnabled.push('deleteWithReason');
    }

    // Polls cannot be edited once published, but their drafts can.
    // Other posts: owner always, admin/super only if marked important.
    const isPoll = post.article_type === ArticleType.POLL;
    const canEdit = isPoll
      ? isPublisher && post.is_draft
      : isPublisher || (isAdmin && post.important);
    if (canEdit) {
      actionsEnabled.push('edit');
    }
    // publisher cannot report his/her own post
    if (!isPublisher) {
      actionsEnabled.push('flag', 'block');
    }
    if ((isPublisher || isAdmin) && post.article_type === 'event') {
      actionsEnabled.push('duplicate');
    }
    if (post.article_type === 'event' && (isAdmin || isPublisher)) {
      actionsEnabled.push('export');

      const eventPost = post as ArticleEvent;
      if (eventPost.external_participation_token) {
        actionsEnabled.push('copyRegistrationLink');
      }
    }

    // only event can add the time to calendar
    if (post.article_type === 'event') {
      actionsEnabled.push('calendar');
    }

    const basicComponentProps: OptionsModalProps = {
      title: _('MODALS.OPTIONS.POST.TITLE'),
      deleteModalProps: {
        title:
          post.article_type == ArticleType.EVENT
            ? _('MODALS.OPTIONS.EVENT.DELETE_TITLE')
            : _('MODALS.OPTIONS.POST.DELETE_TITLE'),
        description:
          post.article_type == ArticleType.EVENT
            ? _('MODALS.OPTIONS.EVENT.DELETE_DESCRIPTION')
            : _('MODALS.OPTIONS.POST.DELETE_DESCRIPTION')
      },
      flagModalProps: {
        title: _('MODALS.OPTIONS.POST.REPORT.TITLE'),
        confirmLabel: _('MODALS.OPTIONS.POST.REPORT.REPORT')
      },
      blockModalProps: {
        title: _('MODALS.OPTIONS.POST.BLOCK.TITLE'),
        confirmLabel: _('MODALS.OPTIONS.POST.BLOCK.BLOCK')
      },
      actionsEnabled,
      isAdmin,
      isEventOwner: isPublisher,
      isEvent: post.article_type === ArticleType.EVENT,
      actionLabels:
        post.article_type === ArticleType.EVENT
          ? {
              deleteWithReason: _('COMMON.LABELS.DELETE')
            }
          : undefined
    };

    const eventExtraProps: OptionsModalProps = {
      flagModalProps: {
        title: _('PAGES.EVENT_DETAIL.OPTION_MODAL.REPORT_CONFIRM.TITLE'),
        confirmLabel: _('PAGES.EVENT_DETAIL.OPTION_MODAL.REPORT_CONFIRM.REPORT')
      },
      blockModalProps: {
        title: _('PAGES.EVENT_DETAIL.OPTION_MODAL.BLOCK_CONFIRM.TITLE'),
        confirmLabel: _('PAGES.EVENT_DETAIL.OPTION_MODAL.BLOCK_CONFIRM.BLOCK')
      }
    };

    // event has specific comfirmation text
    const finalProps: OptionsModalProps =
      post.article_type === 'event'
        ? { ...basicComponentProps, ...eventExtraProps }
        : basicComponentProps;

    const res = await this.modalService.presentAndGetResult(
      SharedModalsUiOptionsModalComponent,
      finalProps
    );
    if (!res) {
      return;
    }

    switch (res.action) {
      case 'edit':
        if (post.article_type === ArticleType.EVENT) {
          this.router.navigate(['/new', 'event', post.id], {
            queryParams: groupParams
          });
        } else if (post.article_type === ArticleType.POLL) {
          this.router.navigate(['/new', 'poll', post.id], {
            queryParams: groupParams
          });
        } else {
          this.router.navigate(['/new', 'post', post.article_type, post.id], {
            queryParams: groupParams
          });
        }
        break;
      case 'duplicate':
        if (this.router.url.includes('calendar')) {
          this.router.navigate(['/new/event'], {
            queryParams: {
              ...groupParams,
              duplicateEventId: post.id,
              redirectTo: 'calendar'
            }
          });
        } else {
          this.router.navigate(['/new/event'], {
            queryParams: { ...groupParams, duplicateEventId: post.id }
          });
        }
        break;
      case 'delete':
        this.sendDeleteRequest(post);
        break;
      case 'deleteWithReason':
        this.handleDeleteWithReason(post, res.reason_for_action);
        break;
      case 'share':
        const url = await this.getAppFrontendUrl();
        this.shareService.share({
          title: post.title,
          url: `${url}/post/${post.id}`
        });
        break;
      case 'flag':
        this.reportService.handleReport({
          itemId: post?.id,
          itemType: 'Article',
          reportType: 'flag',
          reason: res.reason_for_action
        });
        break;
      case 'block':
        this.reportService.handleReport({
          itemId: post?.id,
          itemType: 'Article',
          reportType: 'block',
          reason: res.reason_for_action
        });
        break;
      case 'calendar':
        this.calendarService.createCalendarEvent(post as ArticleEvent);
        break;
      case 'export':
        this.articleRepository
          .exportEventParticipants(post.id)
          .subscribe(blob => {
            this.downloadFile(blob, `event-participants-${post.id}.xlsx`);
          });
        break;
      case 'copyRegistrationLink':
        if (
          'external_participation_token' in post &&
          post.external_participation_token
        ) {
          const registrationUrl = `${window.location.origin}/event-participations?token=${post.external_participation_token}`;
          try {
            await Clipboard.write({
              string: registrationUrl
            });
            this.notificationService.success(
              _('PAGES.EVENT_DETAIL.REGISTRATION_LINK_COPIED')
            );
          } catch (error) {
            this.notificationService.error(
              _('PAGES.EVENT_DETAIL.REGISTRATION_LINK_COPY_FAILED')
            );
          }
        }
        break;
    }
  }

  handleDeleteWithReason(post: Article, reasonForAction: string) {
    if (post.article_type === ArticleType.EVENT) {
      // For events, the reason might be JSON-encoded with both reasons
      try {
        const parsedReasons = JSON.parse(reasonForAction);
        const params: ArticleDeleteParams = {
          reason_for_deletion: parsedReasons.reason_for_deletion,
          cancellation_reason: parsedReasons.cancellation_reason
        };
        this.delete(post.id, params).subscribe(_ => {
          this.router.navigate(['/calendar']);
        });
      } catch {
        // Fallback for single reason (backwards compatibility)
        this.sendDeleteRequest(post, reasonForAction);
      }
    } else {
      // For non-events, use single reason
      this.sendDeleteRequest(post, reasonForAction);
    }
  }

  sendDeleteRequest(
    post,
    modalMessage?,
    messageType: 'deletion' | 'cancellation' = 'deletion'
  ) {
    const params: ArticleDeleteParams = {};

    if (modalMessage) {
      if (messageType === 'cancellation') {
        params.cancellation_reason = modalMessage;
      } else {
        params.reason_for_deletion = modalMessage;
      }
    }

    this.delete(post.id, params).subscribe(_ => {
      // Navigate back to appropriate list after deletion
      if (post.article_type === ArticleType.EVENT) {
        this.router.navigate(['/calendar']);
      } else {
        this.router.navigate(['/home']);
      }
    });
  }

  public downloadFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  getLikes(postId: number): Observable<Like[]> {
    return this.articleRepository.getLikes(postId);
  }
}
